import React, { useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import AppMount from "components/presentation/main/Main.sub.App.view";
import ScreenshotPortal from "components/presentation/main/Main.sub.ScreenshotPortal.view";
import Content from "components/presentation/main/layout/Content";
import { useAppViewController, HostControllerContext } from "components/presentation/main/Main.lib.communication";

// The "Solución" section: the project's own app, mounted whole.
//
// The host used to be the project's router and layout engine — it matched the URL against
// a layout config and placed one view per slot. It no longer is: a project is a single
// React app with its own router, so this component resolves which remote to load, provides
// the bridge around it, and stays out of the way.
//
// It does hand the app this page's history: the project routes on the browser's address
// bar, and everything under /app is its space (this router's own basename), so the app
// pushing /reports/12 shows up as /app/reports/12 and back and forward work.

const Main = ({ components, views, flow }) => {

  const history = useHistory();
  const location = useLocation();
  const {
    registerApp,
    registerChatApi,
    sendControllerAction,
    chats,
    menus,
    openMenus,
    setMenuOpen,
    screenshotRequest,
    finishScreenshot,
  } = useAppViewController({ flow });

  const hostController = useMemo(
    () => ({ registerApp, registerChatApi, sendControllerAction }),
    [registerApp, registerChatApi, sendControllerAction]
  );

  const appView = getAppView(views);

  // A flow with no app published is still an agent, and that is what the section shows —
  // rendered through the same registry the "agent" tab uses, so it is the same screen and
  // not a copy of it.
  if (!appView?.url) return <Content
    components={components}
    currentView={AGENT_VIEW}
    views={views}
    flow={flow}
  />;

  return <HostControllerContext.Provider value={hostController}>
    <AppMount
      flow={flow}
      appView={appView}
      hostHistory={history}
      search={location.search}
      chats={chats}
      menus={menus}
      openMenus={openMenus}
      setMenuOpen={setMenuOpen}
    />
    <ScreenshotPortal
      request={screenshotRequest}
      onFinish={finishScreenshot}
    />
  </HostControllerContext.Provider>;

}

// A project ships exactly one app, stored as its only non-prebuilt view. Everything else
// in `views` is a platform section (agent, data, processes…).
export const getAppView = (views = []) =>
  (views || []).find((view) => !view.is_prebuilt && view.url) || null;

const AGENT_VIEW = { name: "agent" };

export default Main;
